import { FC, useCallback, useRef, useState } from 'react';

import TeamSearch from './shared/TeamSearch';
import CtaButton from '@components/quiz/shared/CtaButton';
import ImportReviewStep from '@components/quiz/shared/ImportReviewStep';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import type { TeamMember } from '@components/manual-team-entry/types';
import type { FplEntrySearchResult } from '@api-service/fpl-entries';
import type { SanityReadinessTeamImportBlock } from '@lib/sanity/types';

type TeamImportBlockProps = SanityReadinessTeamImportBlock & {
  onOcrImported: () => void;
  onEntrySelected: (entry: FplEntrySearchResult) => void;
  onBuildTeam: () => void;
};

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg'];

/**
 * Step 1: team import. Screenshot OCR and team search are both always
 * visible (no tabs). Pre-GW1 leads with the uploader; post-GW1 leads
 * with search ('search-first'). A successful 15-player OCR skips the
 * confirm step; a partial read shows an inline error and stays here.
 */
const TeamImportBlock: FC<TeamImportBlockProps> = ({
  layoutVariant,
  screenshotSectionLabel,
  screenshotUploadLabel,
  screenshotHelpText,
  ocrReadErrorText,
  searchDividerText,
  searchSectionLabel,
  searchPlaceholder,
  searchSelectButtonText,
  buildTeamLinkText,
  ctaText,
  onOcrImported,
  onEntrySelected,
  onBuildTeam
}) => {
  const { state, actions } = useReadiness();

  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setFileError(null);

      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        setFileError('Please upload a PNG or JPG image.');
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileError('File must be under 10MB.');
        return;
      }

      const status = await actions.importTeamFromScreenshot(file, {
        readErrorText: ocrReadErrorText
      });

      if (status === 'imported') onOcrImported();
    },
    [actions, ocrReadErrorText, onOcrImported]
  );

  // The read is rated only once the visitor has confirmed it, so a squad the
  // parser got wrong is corrected here rather than refused.
  const handleReviewConfirm = useCallback(
    async (team: TeamMember[]) => {
      const playerIds = team
        .map(({ player }) => player?.id)
        .filter((id): id is string => Boolean(id));

      const isRated = await actions.confirmTeamFromReview(playerIds);
      if (isRated) onOcrImported();
    },
    [actions, onOcrImported]
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) handleFileSelect(file);
      // Allow re-selecting the same file after a failed read
      event.target.value = '';
    },
    [handleFileSelect]
  );

  const inlineError = fileError ?? state.importError;
  const isSearchFirst = layoutVariant === 'search-first';

  if (state.isReviewingScreenshot && state.screenshotMatches) {
    return (
      <ImportReviewStep
        matches={state.screenshotMatches}
        continueLabel={ctaText ?? 'Continue'}
        backLabel="Start again with a different screenshot"
        isSubmitting={state.isImporting}
        submitError={state.importError}
        onConfirm={handleReviewConfirm}
        onBack={() => actions.setIsReviewingScreenshot(false)}
      />
    );
  }

  const screenshotSection = (
    <>
      {screenshotSectionLabel && (
        <p className="mb-3 font-body text-xs font-bold uppercase tracking-widest text-neutral-500">
          {screenshotSectionLabel}
        </p>
      )}
      <CtaButton
        variant={isSearchFirst ? 'secondary' : 'callout'}
        onClick={() => fileInputRef.current?.click()}
        disabled={state.isImporting}
        dataCy="readiness-screenshot-import"
      >
        {state.isImporting
          ? 'Reading your squad…'
          : screenshotUploadLabel ?? 'Take or upload a screenshot'}
      </CtaButton>
      {screenshotHelpText && (
        <p className="mt-2 text-center font-body text-xs text-neutral-400">
          {screenshotHelpText}
        </p>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </>
  );

  const searchSection = (
    <>
      {searchSectionLabel && (
        <p className="mb-3 font-body text-xs font-bold uppercase tracking-widest text-neutral-500">
          {searchSectionLabel}
        </p>
      )}
      <TeamSearch
        placeholder={searchPlaceholder ?? 'Search by FPL team name or ID'}
        selectButtonText={searchSelectButtonText ?? 'Select'}
        ctaText={ctaText ?? 'Find my team'}
        onSelect={onEntrySelected}
      />
    </>
  );

  const errorLine = inlineError && (
    <p
      className="mt-3 font-body text-sm text-danger-600"
      data-cy="readiness-import-error"
    >
      {inlineError}
    </p>
  );

  const divider = (
    <div className="my-6 flex items-center gap-3">
      <span className="h-px flex-1 bg-neutral-300" />
      <span className="font-body text-sm text-neutral-400">
        {searchDividerText ?? 'or search by name'}
      </span>
      <span className="h-px flex-1 bg-neutral-300" />
    </div>
  );

  return (
    <section className="flex items-start justify-center px-6 pb-16 pt-8">
      <div className="w-full max-w-sm">
        {isSearchFirst ? (
          <>
            {searchSection}
            {divider}
            {screenshotSection}
            {errorLine}
          </>
        ) : (
          <>
            {screenshotSection}
            {errorLine}
            {divider}
            {searchSection}
          </>
        )}

        {/* Manual build branch */}
        {buildTeamLinkText && (
          <button
            type="button"
            onClick={onBuildTeam}
            data-cy="readiness-build-team-link"
            className="mt-4 w-full text-center font-body text-sm font-semibold text-primary-500 transition-colors hover:text-primary-600"
          >
            {buildTeamLinkText}
          </button>
        )}
      </div>
    </section>
  );
};

export default TeamImportBlock;
